import { getAllProfileIds } from "../../src/utilities/api/practitioner-profile";
import React, { useEffect, useMemo, useState } from "react";
import { Text, Button, IconButton, Divider } from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { renderAddresses } from "../../src/utilities/render-helpers/renderPractitionerProfile";
import Link from "next/link";
import { IconChevronLeft, IconChevronRight } from "../../styles/icons";
import {
  useStoryblokState,
  getStoryblokApi,
  StoryblokComponent,
} from "@storyblok/react";

import {
  ViewSectionAbout,
  ViewSectionAvailability,
  ViewSectionClientGroups,
  ViewSectionOfficeMap,
  ViewSectionPracticeAreas,
  ViewSectionPracticeTypes,
  ViewSectionQualifications,
  ViewSectionSessionType,
  ViewSectionProfileHeader,
} from "../../src/practitioner-profile-view";
import LoadingSpinner from "../../src/components/common/LoadingSpinner";
import { IsMobileView } from "../../src/utilities/helpers";
import { ViewSectionAssociations } from "../../src/practitioner-profile-view/ViewSectionAssocations";
import { ViewSectionFees } from "../../src/practitioner-profile-view/ViewSectionFees";
import { useRouter } from "next/router";
import { FaChevronLeft } from "react-icons/fa6";
import Bottleneck from "bottleneck";
import Head from "next/head";
import ArticlePreviewBox from "../../src/articles/ArticlePreviewBox";
import { useMediaQuery } from "react-responsive";

const ViewProfilePage = (props) => {
  // Check screen size
  const mobileView = IsMobileView();
  const router = useRouter();

  // get Profile data
  const { selectedProfile } = props;
  const [profile, setProfile] = useState(selectedProfile);
  const [loading, setLoading] = useState(true);
  const [navButtonJsx, setNavButtonJsx] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const storyblokApi = getStoryblokApi();
  const [articles, setArticles] = useState([]);
  const isSmallScreen = useMediaQuery({ query: "(max-width: 532px)" });

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    const getAuthorsArticles = async () => {
      try {
        const {
          data: { stories: author },
        } = await storyblokApi.get(`cdn/stories`, {
          starts_with: "authors",
          per_page: 1,
          "filter_query[userId][in]": profile.userId,
        });
        const {
          data: { stories },
        } = await storyblokApi.get(`cdn/stories`, {
          starts_with: "article",
          per_page: 4,
          sort_by: "content.legacy_published_at:desc",
          resolve_relations: ["article.authors"],
          "filter_query[authors][any_in_array]": author[0].uuid,
        });
        setArticles(stories);
      } catch (e) {
        console.error(e);
        return {
          notFound: true,
        };
      }
    };
    if (profile.userId) {
      getAuthorsArticles();
    }
    getAuthorsArticles();
  }, [profile]);

  // get search result list
  const searchResults = useSelector((state) => state.profileList?.list);

  useEffect(() => {
    if (searchResults.length > 0) {
      for (let i = 0; i < searchResults.length; i++) {
        if (searchResults[i].pageName === selectedProfile?.pageName) {
          setNavButtonJsx(
            <div className="flex justify-end items-center">
              {i > 0 && (
                <Link
                  href={`/${
                    selectedProfile.practitionerType
                  }/${searchResults[i - 1].pageName}`}
                  passHref
                  prefetch={false}
                >
                  {mobileView ? (
                    <IconButton
                      aria-label="prev"
                      fontSize="24px"
                      icon={<IconChevronLeft />}
                    />
                  ) : (
                    <Button
                      leftIcon={<IconChevronLeft />}
                      variant="link"
                      aria-label="prev"
                    >
                      <Text
                        textStyle="footer2"
                        color="brand.300"
                        fontWeight="medium"
                      >
                        &nbsp;Previous profile
                      </Text>
                    </Button>
                  )}
                </Link>
              )}

              {i < searchResults.length - 1 && (
                <Link
                  href={`/${
                    selectedProfile.practitionerType
                  }/${searchResults[i + 1].pageName}`}
                  passHref
                  prefetch={false}
                >
                  {mobileView ? (
                    <IconButton
                      aria-label="next"
                      fontSize="24px"
                      icon={<IconChevronRight />}
                    />
                  ) : (
                    <Button
                      rightIcon={<IconChevronRight />}
                      variant="link"
                      aria-label="next"
                    >
                      <Text
                        textStyle="footer2"
                        color="brand.300"
                        fontWeight="medium"
                      >
                        Next profile&nbsp;
                      </Text>
                    </Button>
                  )}
                </Link>
              )}
            </div>,
          );
          break;
        }
      }
    }
    // set profile
    setProfile(selectedProfile);
    setLoading(false);
  }, [mobileView, searchResults, selectedProfile]);

  useEffect(() => {
    const isServer = typeof window === "undefined";

    if (!isServer) {
      const detectBack = () => {
        window.localStorage.setItem("wdUsePrev", "true");
      };

      window.addEventListener("popstate", detectBack);
      return () => {
        if (!isServer) {
          window.removeEventListener("popstate", detectBack);
        }
      };
    }
  }, [router]);

  const hasSearch = useMemo(() => {
    const isServer = typeof window === "undefined";
    if (!isServer) {
      const search = window.localStorage.getItem("wdPrevSearch");
      return !!search;
    }
    return false;
  }, []);

  return router.isFallback ? (
    <div className="flex flex-col items-center justify-center bg-white w-full">
      <Spinner size="xl" className="text-brand mb-2" />
      <span className="text-brand-alt font-light">Loading profile...</span>
    </div>
  ) : (
    <div className="flex flex-col items-center bg-white w-full mx-auto">
      <Head>
        <link rel="canonical" href={`https://welldoing.org${router.asPath}`} />
      </Head>
      {!profile && <LoadingSpinner message="Loading profile..." />}
      {hasSearch && isClient && (
        <div className="flex justify-between w-full md:w-[1149px] my-4">
          <Button variant="link" onClick={() => router.back()}>
            <div className="flex items-center text-brand-text">
              <FaChevronLeft className="h-4 w-4" />
              <span className="font-bold text-brand-text">
                Back to search results
              </span>
            </div>
          </Button>
          {navButtonJsx}
        </div>
      )}
      <div className="flex gap-5">
        <div className="w-full flex flex-col gap-5 max-w-[709px]">
          {!!profile && (
            <div className="flex flex-col gap-5">
              <ViewSectionProfileHeader
                profile={profile}
                hasArticles={articles?.length > 0}
              />
              <ViewSectionAbout profile={profile} />
              {mobileView && (
                <>
                  {renderAddresses.length > 0 && (
                    <ViewSectionOfficeMap profile={profile} />
                  )}

                  <ViewSectionSessionType profile={profile} />
                </>
              )}
              <ViewSectionPracticeAreas profile={profile} />
              <ViewSectionFees profile={profile} />
              <ViewSectionAvailability profile={profile} />
              <ViewSectionPracticeTypes profile={profile} />
              <ViewSectionClientGroups profile={profile} />
              <ViewSectionQualifications profile={profile} />
              <ViewSectionAssociations profile={profile} />
              {mobileView && <div className="h-[100px] w-full" />}
              {articles && articles.length > 0 && (
                <div id="articles" style={{ paddingBottom: "20px" }}>
                  <Text textStyle="h3" mt="50px" mb="20px">
                    Articles by this Therapist
                  </Text>
                  <Divider borderWidth="1px" />
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      marginTop: "20px",
                      gap: "20px",
                    }}
                  >
                    {articles.map((article) => (
                      <ArticlePreviewBox
                        author={article?.content?.authors[0].name}
                        imageAlt={article?.content?.headerImage?.alt}
                        title={article?.content?.title}
                        image={article?.content?.headerImage?.filename}
                        slug={article?.slug}
                        key={article?.uuid}
                        width={isSmallScreen ? "100%" : "48%"}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {!mobileView && !!profile && (
          <div className="w-[420px] flex flex-col gap-2">
            {!loading && (
              <>
                <div className="bg-white rounded-[20px] w-full">
                  <ViewSectionSessionType profile={profile} />
                </div>
                {renderAddresses.length > 0 && (
                  <div className="bg-white rounded-[20px] w-full">
                    <ViewSectionOfficeMap profile={profile} />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const limiter = new Bottleneck({
  maxConcurrent: 20,
  minTime: 330,
});

limiter.on("retry", (error, jobInfo) =>
  console.log(`Now retrying ${jobInfo.options.id}`),
);
limiter.on("failed", async (error, jobInfo) => {
  const id = jobInfo.options.id;
  console.warn(`Job ${id} failed: ${error}`);

  if (jobInfo.retryCount === 0) {
    // Here we only retry once
    console.log(`Retrying job ${id} in 25ms!`);
    return 100;
  }
});

const getProfile = async (pageName) => {
  let attempts = 0;
  const maxAttempts = 15;
  let profile = null;
  const initialDelay = Math.random() * 2500 + 1000;

  while (attempts < maxAttempts) {
    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_NEW_SEARCH_URL}profile/${pageName}`,
        {
          method: "GET", // Or 'POST', 'PUT', 'DELETE', etc.
          headers: {
            "x-api-key": process.env.NEXT_PUBLIC_NEW_SEARCH_APIKEY,
          },
        },
      );

      if (res.status === 404) {
        break;
      }
      profile = await res.json();

      // If successful, break the loop
      break;
    } catch (error) {
      attempts++;
      console.error(
        `Attempt ${attempts} for: ${pageName} failed:`,
        error.message,
      );

      // Exponential backoff: wait longer after each failed attempt
      const delay = Math.pow(2, attempts) * initialDelay; // 1s, 2s, 4s...
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  return profile;
};

export async function getStaticProps(context) {
  const { pageName } = context.params;
  try {
    const profile = await limiter.schedule(() => getProfile(pageName));
    if (!profile.userId) {
      console.error("Failed to retrieve therapist: ", pageName);
    }
    return profile.userId
      ? {
          props: {
            selectedProfile: profile,
          },
          revalidate: 1800, // updates every 1 minutes in case of profile changes - should this be quicker in case of calendar changes?
        }
      : {
          notFound: true,
          revalidate: 1800, // Need to keep checking to make sure it does actually come back once the profile exists again
        };
  } catch (error) {
    console.error(`Could not retrieve therapist/${pageName}`);
    return {
      notFound: true,
      revalidate: 1800,
    };
  }
}

export async function getStaticPaths() {
  const allIds = await getAllProfileIds();
  console.info("Get all IDs from", process.env.NEXT_PUBLIC_NEW_SEARCH_URL);
  const paths = allIds
    .filter(({ practitionerType }) => practitionerType === "therapist")
    .map(({ pagePath }) => {
      const removeTherapist = pagePath.replace("therapist/", "");
      return `/counsellors/${removeTherapist}`;
    });
  return {
    paths: paths,
    fallback: "blocking",
  };
}

export default ViewProfilePage;
